import React, { useEffect, useMemo, useState } from 'react';
import {
  insertSkillScore, deleteSkillByName,
  CATEGORY_OPTIONS, CATEGORY_LABELS,
} from './skillData';
import { colors, radius, spacing, fontStack } from '../design-system/tokens';

/**
 * ManageSkillsModal — Phase 9 surface for adding / editing / deleting
 * skill_scores rows.
 *
 * Note on edit semantics: the table is append-only — `insertSkillScore`
 * adds a new row each time, and the Growth page's `getCurrentSkills`
 * helper already picks the latest row per skill_name. So "editing the
 * score" inserts a fresh row with the new score and preserves history.
 *
 * Props:
 *   - open: boolean
 *   - onClose(): close without changes
 *   - userId: string — current user's id (from useGrowthData)
 *   - currentSkills: Array<{ id, skill_name, category, score, recorded_at }>
 *                    one row per skill_name (latest), used for the list
 *                    and dupe-checks
 *   - onChanged(): callback after every successful mutation; the parent
 *                  calls useGrowthData's refresh() so all metrics
 *                  recalculate
 */
export default function ManageSkillsModal({
  open,
  onClose,
  userId,
  currentSkills = [],
  onChanged,
}) {
  // Local view state inside the modal:
  //   'list'  — show all skills, with per-row edit/delete
  //   'add'   — show the add form
  //   'confirm-delete' — show the delete confirm prompt
  const [view, setView] = useState('list');
  const [editingName, setEditingName] = useState(null); // skill_name in inline edit
  const [editScore, setEditScore] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState(null); // { skill_name }
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Add-form state
  const [addName, setAddName] = useState('');
  const [addCategory, setAddCategory] = useState('technical');
  const [addScore, setAddScore] = useState(50);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !editSaving && !addSaving && !deleteSaving) onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, editSaving, addSaving, deleteSaving, onClose]);

  // Reset internal state on open/close so reopening feels fresh.
  useEffect(() => {
    if (!open) {
      setView('list');
      setEditingName(null); setEditScore(''); setEditError(''); setEditSaving(false);
      setConfirmTarget(null); setDeleteSaving(false); setDeleteError('');
      setAddName(''); setAddCategory('technical'); setAddScore(50);
      setAddSaving(false); setAddError('');
    }
  }, [open]);

  const namesLower = useMemo(
    () => new Set(currentSkills.map((s) => String(s.skill_name || '').trim().toLowerCase())),
    [currentSkills]
  );

  if (!open) return null;

  /* ---------- Handlers ---------- */

  const openEdit = (skill) => {
    setEditingName(skill.skill_name);
    setEditScore(String(skill.score ?? ''));
    setEditError('');
  };
  const cancelEdit = () => {
    setEditingName(null); setEditScore(''); setEditError('');
  };
  const saveEdit = async (skill) => {
    const n = Number(editScore);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setEditError('Score must be a number between 0 and 100.');
      return;
    }
    setEditSaving(true); setEditError('');
    try {
      await insertSkillScore({
        user_id: userId,
        skill_name: skill.skill_name,
        category: skill.category,
        score: Math.round(n),
        source: 'self-rated',
      });
      onChanged && (await onChanged());
      cancelEdit();
    } catch (err) {
      setEditError(err?.message || 'Could not save. Try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const askDelete = (skill) => {
    setConfirmTarget(skill);
    setDeleteError('');
  };
  const cancelDelete = () => {
    setConfirmTarget(null); setDeleteError('');
  };
  const confirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleteSaving(true); setDeleteError('');
    try {
      await deleteSkillByName({ user_id: userId, skill_name: confirmTarget.skill_name });
      onChanged && (await onChanged());
      setConfirmTarget(null);
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete. Try again.');
    } finally {
      setDeleteSaving(false);
    }
  };

  const submitAdd = async (e) => {
    if (e) e.preventDefault();
    const name = String(addName || '').trim();
    const score = Number(addScore);

    if (!name) { setAddError('Skill name is required.'); return; }
    if (name.length > 50) { setAddError('Skill name is too long (50 char max).'); return; }
    if (namesLower.has(name.toLowerCase())) {
      setAddError(`You already have a skill called "${name}".`);
      return;
    }
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      setAddError('Score must be a number between 0 and 100.');
      return;
    }

    setAddSaving(true); setAddError('');
    try {
      await insertSkillScore({
        user_id: userId,
        skill_name: name,
        category: addCategory,
        score: Math.round(score),
        source: 'self-rated',
      });
      onChanged && (await onChanged());
      // Reset the form and return to the list.
      setAddName(''); setAddCategory('technical'); setAddScore(50);
      setView('list');
    } catch (err) {
      setAddError(err?.message || 'Could not save. Try again.');
    } finally {
      setAddSaving(false);
    }
  };

  /* ---------- Render ---------- */

  const sortedSkills = [...currentSkills].sort((a, b) =>
    String(a.skill_name).localeCompare(String(b.skill_name))
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Manage skills"
      onClick={() => { if (!editSaving && !addSaving && !deleteSaving) onClose && onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 200,
        fontFamily: fontStack,
      }}
    >
      <div
        className="manage-skills-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: radius.lg,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.textPrimary }}>
            Manage skills
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={editSaving || addSaving || deleteSaving}
            aria-label="Close"
            style={{
              width: 32, height: 32, padding: 0,
              border: 'none', background: 'transparent',
              borderRadius: 999, cursor: 'pointer',
              color: colors.textSecondary, fontSize: 18, fontFamily: fontStack,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '16px 24px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {view === 'add' ? (
            <AddSkillForm
              name={addName} setName={setAddName}
              category={addCategory} setCategory={setAddCategory}
              score={addScore} setScore={setAddScore}
              error={addError}
              saving={addSaving}
              onCancel={() => { setView('list'); setAddError(''); }}
              onSubmit={submitAdd}
              namesLower={namesLower}
            />
          ) : (
            <SkillList
              skills={sortedSkills}
              editingName={editingName}
              editScore={editScore}
              setEditScore={setEditScore}
              editError={editError}
              editSaving={editSaving}
              onStartEdit={openEdit}
              onCancelEdit={cancelEdit}
              onSaveEdit={saveEdit}
              onAskDelete={askDelete}
            />
          )}
        </div>

        {/* Footer / actions */}
        {view === 'list' && (
          <div
            style={{
              padding: '14px 24px',
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => setView('add')}
              style={primaryBtn}
            >
              + Add skill
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation overlay */}
      {confirmTarget && (
        <div
          onClick={cancelDelete}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(17, 24, 39, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 210,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#fff',
              borderRadius: radius.lg,
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
              padding: spacing.lg,
              fontFamily: fontStack,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
              Delete &ldquo;{confirmTarget.skill_name}&rdquo;?
            </h3>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: colors.textSecondary, lineHeight: 1.5 }}>
              This removes every score logged for {confirmTarget.skill_name}, including history.
              This cannot be undone.
            </p>
            {deleteError && (
              <p style={inlineErrorStyle}>{deleteError}</p>
            )}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              marginTop: spacing.base, flexWrap: 'wrap',
            }}>
              <button
                type="button"
                onClick={cancelDelete}
                disabled={deleteSaving}
                style={ghostBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteSaving}
                style={dangerBtn(deleteSaving)}
              >
                {deleteSaving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .manage-skills-card {
            max-width: 100% !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ================================================================
   Skill list (with inline edit + delete buttons per row)
   ================================================================ */

function SkillList({
  skills,
  editingName, editScore, setEditScore, editError, editSaving,
  onStartEdit, onCancelEdit, onSaveEdit, onAskDelete,
}) {
  if (!skills.length) {
    return (
      <div
        style={{
          padding: '24px 8px',
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        No skills yet. Add your first one to get started.
      </div>
    );
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {skills.map((s) => {
        const isEditing = editingName === s.skill_name;
        return (
          <li
            key={s.skill_name}
            style={{
              padding: '12px 4px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div className="manage-skills-row" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <div style={{
                  fontSize: 15, fontWeight: 600, color: colors.textPrimary,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {s.skill_name}
                </div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  {CATEGORY_LABELS[s.category] || 'Other'}
                </div>
              </div>

              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="100"
                    step="1"
                    value={editScore}
                    onChange={(e) => setEditScore(e.target.value)}
                    aria-label={`Score for ${s.skill_name}`}
                    style={{
                      ...inputStyle,
                      width: 84,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => onSaveEdit(s)}
                    disabled={editSaving}
                    style={primarySmallBtn(editSaving)}
                  >
                    {editSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={onCancelEdit}
                    disabled={editSaving}
                    style={ghostSmallBtn}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <div style={{
                    fontSize: 15, fontWeight: 600, color: colors.textPrimary,
                    fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right',
                  }}>
                    {s.score}
                  </div>
                  <button
                    type="button"
                    onClick={() => onStartEdit(s)}
                    aria-label={`Edit ${s.skill_name}`}
                    style={iconBtn}
                  >
                    <PencilGlyph />
                  </button>
                  <button
                    type="button"
                    onClick={() => onAskDelete(s)}
                    aria-label={`Delete ${s.skill_name}`}
                    style={iconBtn}
                  >
                    <TrashGlyph />
                  </button>
                </>
              )}
            </div>
            {isEditing && editError && (
              <p style={inlineErrorStyle}>{editError}</p>
            )}
          </li>
        );
      })}
      <style>{`
        @media (max-width: 480px) {
          .manage-skills-row {
            gap: 8px;
          }
        }
      `}</style>
    </ul>
  );
}

/* ================================================================
   Add form
   ================================================================ */

function AddSkillForm({
  name, setName, category, setCategory, score, setScore,
  error, saving, onCancel, onSubmit, namesLower,
}) {
  const trimmed = String(name || '').trim();
  const isDupe = trimmed && namesLower.has(trimmed.toLowerCase());
  const scoreN = Number(score);
  const scoreOk = Number.isFinite(scoreN) && scoreN >= 0 && scoreN <= 100;
  const canSubmit = trimmed.length > 0 && trimmed.length <= 50 && !isDupe && scoreOk && !saving;

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Skill name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 50))}
          placeholder="e.g. System Design"
          maxLength={50}
          autoFocus
          required
          style={{
            ...inputStyle,
            ...(isDupe ? errorBorder : null),
          }}
        />
        {isDupe && (
          <p style={inlineErrorStyle}>You already have a skill called &ldquo;{trimmed}&rdquo;.</p>
        )}
      </Field>

      <Field label="Category">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={inputStyle}
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Score (0-100)">
        <input
          type="number"
          inputMode="numeric"
          min="0"
          max="100"
          step="1"
          value={score}
          onChange={(e) => setScore(e.target.value)}
          required
          style={{
            ...inputStyle,
            ...(scoreOk || score === '' ? null : errorBorder),
          }}
        />
        {!scoreOk && score !== '' && (
          <p style={inlineErrorStyle}>Score must be a number between 0 and 100.</p>
        )}
      </Field>

      {error && <p style={inlineErrorStyle}>{error}</p>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={saving} style={ghostBtn}>
          Cancel
        </button>
        <button type="submit" disabled={!canSubmit} style={primaryBtn}>
          {saving ? 'Saving…' : 'Save skill'}
        </button>
      </div>
    </form>
  );
}

/* ================================================================
   Tiny helpers
   ================================================================ */

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: fontStack }}>
      <span style={{
        fontSize: 12, fontWeight: 600, color: colors.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function PencilGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4v16h16v-7" />
      <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
function TrashGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

/* ================================================================
   Shared styles
   ================================================================ */

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  minHeight: 40,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 14,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorBorder = { borderColor: 'var(--danger)' };

const inlineErrorStyle = {
  margin: '6px 0 0',
  color: 'var(--danger)',
  fontSize: 12,
  fontFamily: fontStack,
};

const primaryBtn = {
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  border: 'none',
  background: colors.primary,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const primarySmallBtn = (saving) => ({
  ...primaryBtn,
  padding: '8px 14px',
  minHeight: 36,
  fontSize: 13,
  opacity: saving ? 0.6 : 1,
  cursor: saving ? 'wait' : 'pointer',
});

const ghostBtn = {
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const ghostSmallBtn = {
  ...ghostBtn,
  padding: '8px 14px',
  minHeight: 36,
  fontSize: 13,
};

const dangerBtn = (saving) => ({
  padding: '10px 18px',
  minHeight: 44,
  borderRadius: 999,
  border: 'none',
  background: saving ? '#E07765' : 'var(--danger)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: saving ? 'wait' : 'pointer',
});

const iconBtn = {
  width: 32,
  height: 32,
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: 8,
  cursor: 'pointer',
  color: colors.textSecondary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
